'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import type { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form, FormField, FormItem, FormMessage } from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import type { contactSchema } from './contact-schema';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { Field } from './contact-field';
import Link from '@eliadmin/core/src/components/link';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { RiSendPlaneFill } from 'react-icons/ri';

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

export function ContactForm() {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: async (values, context, options) => {
      const [{ zodResolver }, { contactSchema }] = await Promise.all([
        import('@hookform/resolvers/zod'),
        import('./contact-schema'),
      ]);
      return zodResolver(contactSchema(formValidationMessages))(values, context, options);
    },
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      text: '',
      gdpr: true,
      'g-recaptcha-response': '',
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: async (args: Parameters<typeof sendContactMessage>[0]) => {
      const { sendContactMessage } = await import('@eliadmin/core/src/api/forms/forms-api');
      return sendContactMessage(args);
    },
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({
        data: {
          ...data,

        },
        locale,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  const GDPRhref = getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr');

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn('flex w-full flex-col items-start justify-start gap-6', 'ea-contact-form')}
      >
        <div className="flex w-full flex-col items-start justify-start gap-4">
          <Heading title={t('text1346')} className={{ title: 'uppercase' }} />
          <Paragraph className="w-full">{t('text3351')}</Paragraph>
        </div>
        <div
          className={cn('grid w-full grid-cols-12 gap-x-6 gap-y-4', 'ea-contact-form__fields-grid')}
        >
          <div className="col-span-12 w-full border-b border-b-border">
            <Field
              required
              control={form.control}
              name="name"
              label={`${t('text3179')}`}
              className={{
                wrapper: 'col-span-12 w-full',
                main: 'rounded-none pt-3',
              }}
            />
          </div>
          <div className="col-span-12 w-full border-b border-b-border sm:col-span-6">
            <Field
              required
              control={form.control}
              name="email"
              type="email"
              label={'E-mail'}
              className={{
                wrapper: 'col-span-12 w-full sm:col-span-6',
                main: 'rounded-none pt-3',
              }}
            />
          </div>
          <div className="col-span-12 w-full border-b border-b-border sm:col-span-6">
            <Field
              required
              control={form.control}
              name="phone"
              type="tel"
              label={`${t('text1258')}`}
              className={{
                wrapper: 'col-span-12 w-full sm:col-span-6',
                main: 'rounded-none pt-3',
              }}
            />
          </div>

          <div className="col-span-12 w-full border-b border-b-border">
            <Field
              control={form.control}
              name="text"
              type="textarea"
              label={`${t('text2340')}`}
              className={{ wrapper: 'col-span-12 w-full border-0' }}
            />
          </div>

          <Paragraph color="black" className="col-span-12">
            {t('text3353')}&nbsp;
            <Link
              prefetch={false}
              href={GDPRhref}
              className="font-semibold text-primary underline transition-all hover:text-primary/80"
            >
              {t('text1039')}
            </Link>
            &nbsp;{t('text3354')}
          </Paragraph>
        </div>

        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className={cn('hidden')}>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className={cn('w-full sm:w-fit', 'ea-contact-form__submit')}
          isLoading={form.formState.isSubmitting}
        >
          <RiSendPlaneFill size={20} />
          {t('text185')}
        </Button>
      </form>
    </Form>
  );
}
